<!-- eslint-disable vue/no-side-effects-in-computed-properties -->
<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('ADD_COMMS_TEMPLATE')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="communicationTemplate"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="communicationList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(body)="data">
        <p class="mb-0">
          {{ removeHTMLTags(data.value ) }}
        </p>
      </template>
      <template #cell(smsChargePerNotification)="data">
        <p class="mb-0">
          {{ toBalance(data.value ) }}
        </p>
      </template>
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(chargeCustomerSMS)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_COMMS_TEMPLATE')"
              @click="onSelectSmsEmailItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectSmsEmailItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_COMMS_TEMPLATE' : 'ACTIVATE_COMMS_TEMPLATE')"
              @click="communicationStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="communicationRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="name"
                v-model="communicationFormData.name"
                :label="$t('Template Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Template Name')"
                rules="required"
                for="name"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->

            <b-col md="6">
              <SelectField
                id="event-listener"
                v-model="communicationFormData.eventListener"
                :label="$t('Event Listener')"
                :reduce="option=>option"
                :placeholder="$t('Select')"
                :options="eventListenerOptions"
                item="label"
                :error-name="$t('Event Listener')"
                rules="required"
                :readonly="isReadonly"
                :set-selected="loadEventListenerCategories"
              />
              <small
                class="cursor-pointer text-secondary entry-code mb-1"
                style="margin-top: -10px; text-align:right;"
                @click="showModal('modal-eventInformation')"
              >
                {{ $t('Instruction?') }}
              </small>
            </b-col>
          </b-row>

          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="products"
                v-model="communicationFormData.productCodes"
                :reduce="option => option.value"
                :label="$t('Products')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
                multiple
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="channels"
                v-model="communicationFormData.channel"
                :reduce="option => option.value"
                :label="$t('Channel')"
                :placeholder="$t('Select')"
                :options="translatedChannelOptions"
                item="label"
                :readonly="isReadonly"
                :set-selected="setCommunicationChannel"
              />
            </b-col>
          </b-row>
          <b-row class="match-height">
            <b-col md="6">
              <SelectField
                id="entity"
                v-model="communicationFormData.entity"
                :label="$t('Entity')"
                :reduce="option=>option"
                :placeholder="$t('Select')"
                :options="entityOptions"
                item="label"
                :set-selected="getKeywordsByID"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="keyword"
                v-model="communicationFormData.keywords"
                :label="$t('Keyword')"
                :reduce="option=>option"
                :placeholder="$t('Select')"
                :options="keywordsOptions"
                item="label"
                :loading="isLoadingKeywords"
                :readonly="isReadonly"
                :set-selected="onKeywordOptionSelect"
              />
              <small
                class="cursor-pointer text-secondary entry-code mb-1"
                style="margin-top: -10px; text-align:right;"
                @click="showModal('modal-primary')"
              >
                {{ $t('Instruction?') }}
              </small>
            </b-col>
            <b-col md="6">
              <InputField
                id="number-Of-Days-Prior-Reminder"
                v-model="communicationFormData.numberOfDaysPriorReminder"
                :label="$t('Number Of Days Prior Reminder')"
                :placeholder="$t('Enter number Of days prior reminder')"
                type="number"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <InputNumberField
                id="sms-Charge-PerNotification"
                v-model="communicationFormData.smsChargePerNotification"
                :label="$t('SMS Charge Per Notification')"
                :placeholder="$t('Enter number Of days prior reminder')"
                type="number"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col
              md="6"
              class="mb-0 mt-0"
            >
              <SwitchButton
                v-model="communicationFormData.chargeCustomerSMS"
                :label="$t('Charge Customer?')"
                name="charge-customer"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>

          <b-row
            v-if="!isSMSChannel"
            class="match-height"
          >
            <b-col md="12">
              <InputField
                id="subject"
                v-model="communicationFormData.subject"
                :label="$t('Subject')"
                :placeholder="$t('Enter subject')"
                type="text"
                :error-name="$t('Subject')"
                rules="required"
                for="subject"
                :readonly="isReadonly"
                onpaste="return false;"
              />
            </b-col>
          </b-row>

          <!-- Message Body -->
          <b-row class="match-height">
            <b-col
              v-if="isSMSChannel"
              md="12"
              class="mb-3"
            >
              <TextAreaField
                id="editor"
                v-model="communicationFormData.messageBody"
                placeholder=""
                rows="6"
                rules="required"
                :error-name="$t('Message Body')"
                :label="$t('Message Body')"
                :readonly="isReadonly"
                :disabled="isDisabled"
              />
            </b-col>
            <b-col
              v-else
              md="12"
              class="mb-3"
            >
              <RichTextAreaField
                id="editor"
                v-model="communicationFormData.messageBody"
                placeholder=""
                :label="$t('Message Body')"
                :error-name="$t('Message Body')"
                rules="required"
                :readonly="isReadonly"
                :disabled="isDisabled"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="filterCommunications"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Template Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="event-listener"
            v-model="filter.eventListener"
            :label="$t('Event Listener')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="eventListenerOptions"
            item="label"
          />
        </b-col>

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="trnslatedStatusOption"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <!-- Instruction modal -->
    <b-modal
      id="modal-primary"
      ok-only
      :ok-title="$t('Close')"
      centered
      title="Instruction on keyword"
    >
      <b-card-text>
        <p>
          {{ $t('A keyword is automatically copied to clipboard as soon as it\'s selected from the dropdown list. Paste on the message textbox when creating your message.') }}
        </p>
      </b-card-text>
    </b-modal>
    <!-- Instruction modal -->
    <!-- Event Instructions -->
    <b-modal
      id="modal-eventInformation"
      ok-only
      :ok-title="$t('Close')"
      centered
      title="Instruction on Event Listener"
    >
      <b-card-text>
        <p>
          {{ $t('Please note, when an Event Listener is changed, it will reset the message body to empty.') }}
        </p>
      </b-card-text>
    </b-modal>
    <!-- Event Instructions -->

  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BCardText, BBadge,
  BModal, BForm, BDropdownItem, BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { toastAlert } from '@core/mixins/toast'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import removeHTMLTags from '@/helpers/removeHTMLTags'
import InputField from '../../../forms/InputField.vue'
import InputNumberField from '../../../forms/InputNumberField.vue'
import SelectField from '../../../forms/SelectField.vue'
import TextAreaField from '../../../forms/TextAreaField.vue'
import RichTextAreaField from '../../../forms/RichTextAreaField.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import { operationPermissions } from '../../../../helpers/permissions'
import { toBalance, toKobo } from '../../../../helpers/formatAmount'
import { transformBoolean } from '../../../../helpers/transformers'

const channelOptions = [
  { label: 'SMS', value: 'SMS' },
  { label: 'Email', value: 'Email' },
]

const statusOption = [
  { label: 'Active', value: 'Active' },
  { label: 'InActive', value: 'InActive' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    SelectField,
    BTable,
    BModal,
    BCardText,
    BForm,
    BSpinner,
    ValidationObserver,
    InputField,
    InputNumberField,
    SwitchButton,
    BDropdownItem,
    BDropdown,
    TablePagination,
    Button,
    TableEntries,
    TextAreaField,
    RichTextAreaField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert, permissionMixin],
  data() {
    return {
      skipNextEventListenerLoad: false,
      query: '',
      toBalance,
      isReadonly: false,
      isEditable: false,
      isDisabled: false,
      isSMSChannel: false,
      isLoadingKeywords: false,
      sortBy: 'name',
      buttonLabel: this.$t('Save'),
      required,
      getStatusColor,
      modalTitle: this.$t('Create New SMS & Email Template'),
      sortDesc: false,
      removeHTMLTags,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Template Name' },
        { key: 'channel', sortable: true, label: 'Channel' },
        { key: 'eventListener', sortable: true, label: 'Event Listener' },
        { key: 'body', sortable: true, label: 'Message Body' },
        { key: 'chargeCustomerSMS', sortable: true, label: 'Charge Customer' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      messageWordCount: 0,
      limit: 160,
      eventListenerOptions: [],
      entityOptions: [],
      keywordsOptions: [],
      productOptions: [],
      // channelOptions: ['SMS', 'Email'],
      // statusOption: [
      //   'Active',
      //   'InActive',
      // ],
      communicationFormData: {
        name: '',
        eventListener: '',
        productCodes: [],
        channel: '',
        chargeCustomerSMS: false,
        entity: '',
        keywords: '',
        numberOfDaysPriorReminder: 0,
        smsChargePerNotification: 0,
        subject: '',
        messageBody: '',
        id: '',
      },
      filter: {
        name: '',
        eventListener: '',
        status: '',
        sortColumn: '',
      },
      communicationList: [],
      communicationItems: [],
      communicationData: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedChannelOptions() {
      return channelOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    trnslatedStatusOption() {
      return statusOption.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCommunications()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCommunications()
    },
  },
  mounted() {
    this.fetchCommunications()
    this.getEventNameOptions()
    this.fetchProduct()
  },

  methods: {
    ...mapActions({
      getCommunication: 'operations/getCommunicationList',
      updateCommunication: 'operations/updateCommunicationApiAction',
      createCommunication: 'operations/createCommunicationApiAction',
      getEventNames: 'operations/getEventNames',
      getEntityByEntityListenerIndex: 'operations/getEntityByEntityListenerIndex',
      getEntityByObjectID: 'operations/getEntityByObjectID',
      updateCommunicationStatus: 'operations/updateCommunicationStatus',
      getProduct: 'account/getProducts',
    }),
    setCommunicationChannel(value) {
      if (value === 'SMS') {
        this.isSMSChannel = true
      } else {
        this.isSMSChannel = false
      }
    },
    async onSelectSmsEmailItem(items, activity, val) {
      this.skipNextEventListenerLoad = true
      if (val === 'view') {
        this.isDisabled = true
      } else {
        this.isDisabled = false
      }
      this.isEditable = true
      this.communicationFormData.name = items.name
      this.communicationFormData.eventListener = items.eventListener
      this.communicationFormData.productCodes = items.productCodes.split(',').map(Number)
      this.communicationFormData.channel = items.channel
      this.communicationFormData.numberOfDaysPriorReminder = items.numberOfDaysPriorReminder
      this.communicationFormData.smsChargePerNotification = items.smsChargePerNotification ? toBalance(items.smsChargePerNotification) : ''
      this.communicationFormData.chargeCustomerSMS = items.chargeCustomerSMS === 'Yes'
      this.communicationFormData.subject = items.subject
      this.communicationFormData.messageBody = items.body
      this.communicationFormData.id = items.id
      this.isEditable = true
      this.isSMSChannel = items.channel === 'SMS' ? this.isSMSChannel = true : this.isSMSChannel = false
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit SMS & Email Template') : this.$t('View SMS & Email Template')
      this.buttonLabel = this.$t('Update')
      this.$nextTick(() => {
        this.skipNextEventListenerLoad = false
      })
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async communicationAction() {
      try {
        this.isLoading = true
        const formData = {
          name: this.communicationFormData.name,
          eventListener: this.communicationFormData.eventListener.label,
          channel: this.communicationFormData.channel,
          productCodes: this.communicationFormData.productCodes.toString(),
          chargeCustomerSMS: this.communicationFormData.chargeCustomerSMS,
          numberOfDaysPriorReminder: this.communicationFormData.numberOfDaysPriorReminder ? Number(this.communicationFormData.numberOfDaysPriorReminder) : 0,
          smsChargePerNotification: this.communicationFormData.smsChargePerNotification ? toKobo(Number(this.communicationFormData.smsChargePerNotification)) : 0,
          subject: this.communicationFormData.subject,
          body: this.communicationFormData.messageBody,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createCommunication(formData)
          : await this.updateCommunication({
            id: this.communicationFormData.id,
            name: this.communicationFormData.name,
            eventListener: this.communicationFormData.eventListener.label,
            productCodes: this.communicationFormData.productCodes.toString(),
            numberOfDaysPriorReminder: this.communicationFormData.numberOfDaysPriorReminder ? Number(this.communicationFormData.numberOfDaysPriorReminder) : 0,
            smsChargePerNotification: this.communicationFormData.smsChargePerNotification ? toKobo(Number(this.communicationFormData.smsChargePerNotification)) : 0,
            channel: this.communicationFormData.channel,
            chargeCustomerSMS: this.communicationFormData.chargeCustomerSMS,
            subject: this.communicationFormData.subject,
            body: this.communicationFormData.messageBody,
          })

        const { status } = await response
        if (status === 200) {
          const yourRequest = this.$t('Your request to')
          const successText = this.$t('SMS & Email Template was successful')
          this.fetchCommunications()
          this.hideModal('modal-lg')
          this.alertSuccess({ text: `${yourRequest} ${this.buttonLabel} ${successText}` })
        }
      } catch (error) {
        this.hideModal('modal-lg')
        const yourRequest = this.$t('Your request to')
        const failedText = this.$t('SMS & Email Template failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${yourRequest} ${this.buttonLabel} ${failedText} ${responseError.errorMessage}` })
        })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async handleSubmit(bvModalEvt) {
      try {
        bvModalEvt.preventDefault()
        this.$refs.communicationRules.validate().then(async success => {
          if (success) {
            const areYouSure = this.$t('Are you sure you want to')
            const thisText = this.$t('this SMS & Email Template?')
            this.confirm(this.communicationAction, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
          }
        })
      } catch (error) {
        this.hideModal('modal-lg')
        this.alertError({ text: error })
      } finally {
        this.hideModal('modal-lg')
      }
    },
    async fetchCommunications() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getCommunication({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.communicationList = data.data.map(items => ({
            ...items,
            chargeCustomerSMS: transformBoolean(items.chargeCustomerSMS),
          }))
          this.communicationItems = data.data
          this.totalCount = data.recordCount
        } else {
          this.communicationList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    filterCommunications() {
      this.communicationData = this.communicationItems
      if (this.filter.name !== '' || this.filter.eventListener.label !== '' || this.filter.status) {
        const items = this.communicationData.filter(item => item.name === this.filter.name || item.eventListener === this.filter.eventListener.label || item.status === this.filter.status)
        // eslint-disable-next-line no-return-assign
        return this.communicationList = items
      }
      // eslint-disable-next-line no-return-assign
      return this.communicationList = this.communicationItems
    },
    async getEventNameOptions() {
      try {
        const response = await this.getEventNames()
        const data = await response.data
        // eslint-disable-next-line no-nested-ternary
        const sortedData = data.data.sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1))
        this.eventListenerOptions = sortedData.map(item => ({ label: item.description, value: item.name, index: item.index }))
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async loadEventListenerCategories(selectedItem) {
      try {
        if (this.skipNextEventListenerLoad) {
          return
        }
        const eventIndex = selectedItem.index
        const response = await this.getEntityByEntityListenerIndex(eventIndex)
        const data = await response.data
        this.entityOptions = data.data.map(item => ({ label: item.description, value: item.objectID }))
        this.communicationFormData.messageBody = ''
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.skipNextEventListenerLoad = false
      }
    },
    async getKeywordsByID(dataObject) {
      try {
        this.communicationFormData.keywords = ''
        this.isLoadingKeywords = true
        const objectID = dataObject.value
        const response = await this.getEntityByObjectID(objectID)
        const data = await response.data
        this.keywordsOptions = data.data.map(item => ({ label: item.description, value: item.keyword }))
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingKeywords = false
      }
    },
    onKeywordOptionSelect(event) {
      const dataValue = event.value
      navigator.clipboard.writeText(dataValue)
      this.toastAlert({
        title: this.$t('Success'),
        icon: 'CoffeeIcon',
        variant: 'success',
        text: this.$t('Copied'),
      })
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: operationPermissions.VIEW_COMMS_TEMPLATE,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async communicationStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_COMMS_TEMPLATE' : 'ACTIVATE_COMMS_TEMPLATE'
      const yourRequest = this.$t('Your request to')
      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateCommunicationStatus({
            statusValues, actionType, permission,
          })
          if (response.status === 200) {
            this.fetchCommunications()
            const text1 = this.$t('was successful')
            this.alertSuccess({ text: `${yourRequest} ${action} ${items.name} ${text1}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const thisText = this.$t('this SMS & Email Template?')
        this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
      } catch (error) {
        const reasonText = this.$t('SMS & Email Template failed. Reason:')
        this.alertError({ text: `${yourRequest} ${action} ${reasonText} ${error}` })
      } finally {
        this.showSpinner = false
      }
    },

    resetModal() {
      this.communicationFormData = { ...null }
      this.isEditable = false
      this.isLoading = false
      this.isSMSChannel = false
      this.isReadonly = false
      this.modalTitle = this.$t('Create New SMS & Email Template')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCommunications()
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.message-counter {
  position: absolute;
  left: 20px;
  bottom: 20px;
  font-size: 12px;
}
</style>
